import { registerLocaleData } from '@angular/common';
import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  GuardsCheckEnd,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import {
  APP_AVAILABLE_LANGUAGES,
  APP_LOCALE_DATA,
  CurrencyService,
  ENVIRONMENT,
  Environment,
  LanguageService,
  LocaleService,
  RouteDataHelper,
  SharedService,
  UserService
} from '@cfp/core';
import { CustomerStyling } from '@cfp/features/customer-styling';
import { ChartLoader } from '@cfp/shared/ui/chart';
import { LoadingSpinner } from '@cfp/ui/common';
import { TranslateService } from '@ngx-translate/core';
import { LanguageCodeDTO } from 'csr-login-app';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { tap } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [LoadingSpinner, CustomerStyling, RouterOutlet]
})
export class AppComponent {
  public get hasUser(): boolean {
    return !!this.userService.user();
  }

  private shouldInitLanguage: boolean = true;
  private queryParams: URLSearchParams;

  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly meta = inject(Meta);
  private readonly translateService = inject(TranslateService);
  private readonly availableLanguages = inject(APP_AVAILABLE_LANGUAGES) || this.environment.availableLanguages;
  private readonly localeData = inject(APP_LOCALE_DATA, { optional: true });
  private readonly userService = inject(UserService);
  private readonly languageService = inject(LanguageService);
  private readonly currencyService = inject(CurrencyService);
  private readonly routeDataHelper = inject(RouteDataHelper);
  private readonly localeService = inject(LocaleService);
  private readonly sharedService = inject(SharedService);
  private readonly chartLoader = inject(ChartLoader);

  protected readyLoadingModules = this.sharedService.readyLoadingModules;

  protected readonly loading = computed(() => !this.readyLoadingModules() || !this.readyLoadingLanguage());

  private readyLoadingLanguage = signal(false);

  constructor() {
    const queryString: string = window.location.search;
    this.queryParams = new URLSearchParams(queryString);

    this.useCustomerStyling();

    if (this.queryParams.has('page')) {
      this.router.navigate([this.queryParams.get('page')]);
    }

    if (this.environment.currencyDisplay) {
      this.currencyService.preferredCurrencyDisplay.set(this.environment.currencyDisplay);
    }

    if (!this.environment.preloadModules || this.environment.preloadModules.length === 0) {
      this.sharedService.readyLoadingModules.set(true);
    }

    this.router.events
      .pipe(
        tap((event) => {
          if (event instanceof NavigationEnd) {
            this.onNavigationChange();
          } else if (this.shouldInitLanguage && event instanceof GuardsCheckEnd) {
            this.shouldInitLanguage = false;
            this.initLanguage(event.state.root, this.queryParams);
          }
        })
      )
      .subscribe();

    this.updateDocumentTitle();

    this.chartLoader.load();
  }

  @HostListener('document:wheel')
  public onWheel(): void {
    if ((document.activeElement as HTMLInputElement).type === 'number') {
      // Prevent scroll wheel to change number input
      (document.activeElement as HTMLInputElement).blur();
    }
  }

  public useCustomerStyling(): boolean {
    return this.environment.useCustomerStyling || false;
  }

  private onNavigationChange(): void {
    // Handle actions that needs to be done after the navigation change
    this.updateDocumentTitle();
    this.triggerFathomTrackPageView();
  }

  private triggerFathomTrackPageView(): void {
    // We need to trigger fathom.trackPageview on router change - else pages are not tracked
    if ((window as any).fathom?.trackPageview && this.environment.addTagManagement) {
      (window as any).fathom.trackPageview();
    }
  }

  private updateDocumentTitle(): void {
    const appendPageTitle = this.routeDataHelper.getDataFromRoute('appendPageTitle', this.activatedRoute.snapshot);
    if (appendPageTitle) {
      document.title = `${this.getTranslatedAppName()} | ${this.translateService.instant(appendPageTitle)}`;
      return;
    }
    const prependPageTitle = this.routeDataHelper.getDataFromRoute('prependPageTitle', this.activatedRoute.snapshot);
    if (prependPageTitle) {
      document.title = `${this.translateService.instant(prependPageTitle)} | ${this.getTranslatedAppName()}`;
      return;
    }
    const pageTitle = this.routeDataHelper.getDataFromRoute('pageTitle', this.activatedRoute.snapshot);
    if (pageTitle) {
      document.title = pageTitle;
      return;
    }
    document.title = this.getTranslatedAppName();
  }

  private getTranslatedAppName(): string {
    if (this.environment.appNameTranslationKey) {
      const appName = this.translateService.instant(this.environment.appNameTranslationKey);
      if (appName !== this.environment.appNameTranslationKey) {
        return appName;
      }
    }
    return this.environment.appName;
  }

  private initLanguage(activatedRouteSnapshot: ActivatedRouteSnapshot, params?: URLSearchParams): void {
    let preferredFallbackLanguageCode: string | undefined;
    if (params?.has('language')) {
      preferredFallbackLanguageCode = params.get('language');
    } else if (this.environment.initialLanguage) {
      preferredFallbackLanguageCode = this.environment.initialLanguage;
    }

    const availableLanguagesFromData = this.routeDataHelper.getDataFromRoute(
      'availableLanguages',
      activatedRouteSnapshot
    );
    const availableLanguages = availableLanguagesFromData || this.availableLanguages;

    this.translateService.addLangs(availableLanguages);

    this.languageService.getSupportedLanguage(preferredFallbackLanguageCode).subscribe({
      next: (languageCode: LanguageCodeDTO | undefined) => {
        if (this.translateService.store.translations[this.translateService.currentLang]) {
          this.onLanguageChange();
        } else {
          this.translateService.onLangChange.subscribe(() => {
            if (this.translateService.store.translations[languageCode]) {
              this.onLanguageChange();
            }
          });
        }

        if (!languageCode) {
          // No language set or no translation available for language, use default from environment
          languageCode = this.environment.localeId;
        }

        this.languageService.setLanguageAndReloadWhenNeeded(languageCode);

        this.initLocale(languageCode);
        this.updateDocumentTitle();
      },
      error: (error) => {
        console.log('Error', error);
      }
    });
  }

  private initLocale(languageCode: LanguageCodeDTO): void {
    if (!this.localeData) {
      return;
    }

    const localeId = this.localeService.getLocaleId(languageCode);
    const localeData = this.localeData[languageCode] || this.localeData[localeId];
    if (!localeData) {
      return;
    }

    if (localeData.localeDataAngular) {
      registerLocaleData(localeData.localeDataAngular, localeId);
    }
    if (localeData.localeDataBootstrap) {
      defineLocale(localeData.localeDataBootstrap.abbr || localeId, localeData.localeDataBootstrap);
    }
  }

  private onLanguageChange(): void {
    this.readyLoadingLanguage.set(true);
    this.meta.updateTag({ name: 'description', content: this.translateService.instant('head.description') });
    document.documentElement.lang = this.translateService.currentLang;

    this.updateDocumentTitle();
  }
}
